import { useEffect, useMemo, useRef, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { API_BASE_URL } from '../utils/api'
import { useAuth } from './AuthContext'

export interface RealtimeNotification<T = any> {
  type?: string
  payload?: T
  [key: string]: any
}

interface UseNotificationsOptions {
  namespace?: string // defaults to /admin/monitoring
  autoConnect?: boolean
}

export function useNotifications<T = any>(options: UseNotificationsOptions = {}) {
  const { token } = useAuth()
  const namespace = options.namespace ?? '/admin/monitoring'
  const [connected, setConnected] = useState(false)
  const [notifications, setNotifications] = useState<RealtimeNotification<T>[]>([])
  const socketRef = useRef<Socket | null>(null)

  const url = useMemo(() => {
    // Socket.IO namespace URL
    // Example: http://localhost:3000/admin/monitoring
    return `${API_BASE_URL}${namespace}`
  }, [namespace])

  useEffect(() => {
    if (!token) return

    const socket = io(url, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: { token },
      extraHeaders: {
        // If you need multi-tenant header in handshake requests
        // 'x-tenant-id': import.meta.env.VITE_TENANT_ID || ''
      },
      autoConnect: options.autoConnect ?? true,
    })

    socketRef.current = socket

    socket.on('connect', () => setConnected(true))
    socket.on('disconnect', () => setConnected(false))

    socket.on('notification', (msg: RealtimeNotification<T>) => {
      const stableId = msg.id || `notif-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
      setNotifications(prev => [{ ...msg, id: stableId }, ...prev].slice(0, 200))
    })

    // Optional: listen to job/worker events if the backend emits them
    const jobEvents = ['job:waiting', 'job:active', 'job:completed', 'job:failed'] as const
    jobEvents.forEach(evt => {
      socket.on(evt, (msg: any) => {
        const jobId = msg?.jobId ? `-${msg.jobId}` : ''
        const stableId = `job-${evt}${jobId}-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`
        setNotifications(prev => [{ id: stableId, type: evt, payload: msg }, ...prev].slice(0, 200))
      })
    })

    return () => {
      jobEvents.forEach(evt => socket.off(evt))
      socket.off('notification')
      socket.disconnect()
      socketRef.current = null
    }
  }, [token, url])

  const emitNotification = (payload: any) => {
    socketRef.current?.emit('notification', { payload })
  }

  return {
    connected,
    notifications,
    emitNotification,
    socket: socketRef.current,
  }
}

export default useNotifications
